import { API_BASE_URL } from '../config/apiBase';
import { ensureCsrfToken, isPlausibleCsrfToken, readCsrfCookie } from './csrf';

const API_BASE = API_BASE_URL;

let activeTenantId = null;
const tenantListeners = new Set();
export function subscribeTenant(listener) {
  tenantListeners.add(listener);
  return () => tenantListeners.delete(listener);
}

export function setTenantId(tenantId) {
  const next = tenantId || null;
  if (next === activeTenantId) return;
  activeTenantId = next;
  tenantListeners.forEach((listener) => listener(next));
}

export function getTenantId() {
  return activeTenantId;
}

async function apiRequest(path, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
  };
  if (options.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const method = (options.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    let csrf = readCsrfCookie();
    if (!isPlausibleCsrfToken(csrf)) {
      csrf = await ensureCsrfToken();
    }
    headers['X-CSRFToken'] = csrf;
  }
  if (activeTenantId) {
    headers['X-Tenant-Id'] = activeTenantId;
  }

  const response = await fetch(`${API_BASE}/v1${path}`, {
    credentials: 'include',
    ...options,
    headers,
  });

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (err) {
      data = { error: { code: 'invalid_json', message: text } };
    }
  }

  if (!response.ok) {
    const error = new Error(
      (data && data.error && data.error.message) || `Request failed (${response.status})`
    );
    error.status = response.status;
    error.code = data && data.error ? data.error.code : 'http_error';
    error.details = data && data.error ? data.error.details : null;
    error.payload = data;
    throw error;
  }
  return data;
}

export const bakingApi = {
  setTenantId,
  getTenantId,
  listProjects: () => apiRequest('/projects/'),
  createProject: (body) =>
    apiRequest('/projects/', { method: 'POST', body: JSON.stringify(body) }),
  listProductionRecords: (projectId) => apiRequest(`/projects/${projectId}/production-records/`),
  getProductionRecord: (projectId, id) => apiRequest(`/projects/${projectId}/production-records/${id}/`),
  createProductionRecord: (projectId, body) => apiRequest(`/projects/${projectId}/production-records/`, { method: 'POST', body: JSON.stringify(body) }),
  saveProductionRecord: (projectId, id, body) => apiRequest(`/projects/${projectId}/production-records/${id}/`, { method: 'PUT', body: JSON.stringify(body) }),
  listProductionNodeRuns: (projectId, recordId, { nodeId, page = 1, pageSize = 50 } = {}) => {
    const qs = new URLSearchParams();
    if (nodeId) qs.set('node_id', nodeId);
    if (page) qs.set('page', String(page));
    if (pageSize) qs.set('page_size', String(pageSize));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiRequest(`/projects/${projectId}/production-records/${recordId}/runs/${suffix}`);
  },
  createProductionNodeRun: (projectId, recordId, nodeId, body = {}) =>
    apiRequest(`/projects/${projectId}/production-records/${recordId}/nodes/${encodeURIComponent(nodeId)}/runs/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  exportCharacterPackage: (projectId, recordId, nodeId, body = {}) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/nodes/${encodeURIComponent(nodeId)}/export/`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    ),
  getWorkflowExecutionPlan: (projectId, recordId) =>
    apiRequest(`/projects/${projectId}/production-records/${recordId}/execution-plan/`),
  runWorkflowExecutionPlan: (projectId, recordId, body = {}) =>
    apiRequest(`/projects/${projectId}/production-records/${recordId}/execution-plan/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getWorkflowNodeEstimate: (projectId, recordId, nodeId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/nodes/${encodeURIComponent(nodeId)}/estimate/`
    ),
  getProductionNodeSummary: (projectId, recordId, nodeId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/nodes/${encodeURIComponent(nodeId)}/summary/`
    ),
  getProductionNodeRun: (projectId, recordId, runId) =>
    apiRequest(`/projects/${projectId}/production-records/${recordId}/runs/${runId}/`),
  getProject: (id) => apiRequest(`/projects/${id}/`),
  listProjectFiles: (projectId, params) => apiRequest(`/projects/${projectId}/files/?${new URLSearchParams(params)}`),
  listProjectFolders: (projectId, params = {}) =>
    apiRequest(`/projects/${projectId}/folders/?${new URLSearchParams(params)}`),
  updateProjectImage: (projectId, imageId, body) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteProjectImage: (projectId, imageId, body = {}) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
  initiateProjectImageReplace: (projectId, imageId, body) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/replace/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  confirmProjectImageReplace: (projectId, imageId, body = {}) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/replace/confirm/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateProjectRevisionFile: (projectId, revisionId, body) =>
    apiRequest(`/projects/${projectId}/revisions/${revisionId}/file/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteProjectRevisionFile: (projectId, revisionId, body = {}) =>
    apiRequest(`/projects/${projectId}/revisions/${revisionId}/file/`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
  listProjectImages: (projectId) => apiRequest(`/projects/${projectId}/images/`),
  initiateProjectImageUpload: (projectId, body) =>
    apiRequest(`/projects/${projectId}/images/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  confirmProjectImageUpload: (projectId, imageId) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/confirm/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  getProjectImage: (projectId, imageId) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/`),
  createProjectImageDownloadAuth: (projectId, imageId) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/download-auth/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  /** Short-lived URL for the platform-managed JPEG thumbnail (not the full original). */
  createProjectImagePreviewAuth: (projectId, imageId) =>
    apiRequest(`/projects/${projectId}/images/${imageId}/preview-auth/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  getStudioHistory: (projectId, recordId) =>
    apiRequest(`/projects/${projectId}/production-records/${recordId}/studio-history/`),
  getStudioCheckpoint: (projectId, recordId, checkpointId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/checkpoints/${checkpointId}/`
    ),
  restoreStudioCheckpoint: (projectId, recordId, checkpointId, body = {}) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/checkpoints/${checkpointId}/restore/`,
      { method: 'POST', body: JSON.stringify(body) }
    ),
  setStudioBranchFavorite: (projectId, recordId, branchId, favorite) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/branches/${branchId}/favorite/`,
      { method: 'POST', body: JSON.stringify({ favorite }) }
    ),
  previewStudioBranchCleanup: (projectId, recordId, branchId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/branches/${branchId}/cleanup-preview/`
    ),
  removeStudioBranch: (projectId, recordId, branchId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/branches/${branchId}/remove/`,
      { method: 'POST', body: JSON.stringify({}) }
    ),
  recoverStudioBranch: (projectId, recordId, branchId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/branches/${branchId}/recover/`,
      { method: 'POST', body: JSON.stringify({}) }
    ),
  listStudioRecoverable: (projectId, recordId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/recoverable/`
    ),
  processStudioDraft: (projectId, recordId, body = {}) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/process/`,
      { method: 'POST', body: JSON.stringify(body) }
    ),
  retryStudioAttempt: (projectId, recordId, attemptId) =>
    apiRequest(
      `/projects/${projectId}/production-records/${recordId}/studio-history/attempts/${attemptId}/retry/`,
      { method: 'POST', body: JSON.stringify({}) }
    ),
  listRevisions: (projectId) => apiRequest(`/projects/${projectId}/revisions/`),
  getRevision: (revisionId) => apiRequest(`/project-revisions/${revisionId}/`),
  initiateRevisionUpload: (projectId, body, idempotencyKey) =>
    apiRequest(`/projects/${projectId}/revisions/uploads/`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    }),
  confirmRevisionUpload: (revisionId, body) =>
    apiRequest(`/project-revisions/${revisionId}/confirm-upload/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listJobArtifacts: (jobId) => apiRequest(`/bake-jobs/${jobId}/artifacts/`),
  createArtifactDownloadAuth: (artifactId) =>
    apiRequest(`/artifacts/${artifactId}/download-auth/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  listScenes: (projectId) => apiRequest(`/projects/${projectId}/scenes/`),
  createScene: (projectId, body) =>
    apiRequest(`/projects/${projectId}/scenes/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listBakeProfiles: (projectId) => {
    const q = projectId ? `?project_id=${projectId}` : '';
    return apiRequest(`/bake-profiles/${q}`);
  },
  listJobs: () => apiRequest('/bake-jobs/'),
  createJob: (body, idempotencyKey) =>
    apiRequest('/bake-jobs/', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
    }),
  getJob: (id) => apiRequest(`/bake-jobs/${id}/`),
  listJobEvents: (id) => apiRequest(`/bake-jobs/${id}/events/`),
  listCompatibilityPolicies: () => apiRequest('/compatibility/policies/'),
  listExecutionProviders: () => apiRequest('/execution-providers/'),
  getRevisionCompatibility: (revisionId) =>
    apiRequest(`/project-revisions/${revisionId}/compatibility/`),
  analyzeRevisionCompatibility: (revisionId, body = {}) =>
    apiRequest(`/project-revisions/${revisionId}/compatibility/analyze/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getRevisionCompatibilityHistory: (revisionId) =>
    apiRequest(`/project-revisions/${revisionId}/compatibility/history/`),
  acknowledgeRevisionCompatibility: (revisionId, body) =>
    apiRequest(`/project-revisions/${revisionId}/compatibility/acknowledge/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listAiConversations: () => apiRequest('/ai/conversations/'),
  createAiConversation: (body) =>
    apiRequest('/ai/conversations/', { method: 'POST', body: JSON.stringify(body) }),
  getAiConversation: (id) => apiRequest(`/ai/conversations/${id}/`),
  listAiMessages: (conversationId) =>
    apiRequest(`/ai/conversations/${conversationId}/messages/`),
  postAiMessage: (conversationId, body) =>
    apiRequest(`/ai/conversations/${conversationId}/messages/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getAiRun: (runId) => apiRequest(`/ai/runs/${runId}/`),
  listAiRunEvidence: (runId) => apiRequest(`/ai/runs/${runId}/evidence/`),
  listAiRecommendations: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const query = qs.toString();
    return apiRequest(`/ai/recommendations/${query ? `?${query}` : ''}`);
  },
  getAiRecommendation: (id) => apiRequest(`/ai/recommendations/${id}/`),
  reviewAiRecommendation: (id, body = {}) =>
    apiRequest(`/ai/recommendations/${id}/review/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  acceptAiRecommendation: (id, body = {}) =>
    apiRequest(`/ai/recommendations/${id}/accept/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  dismissAiRecommendation: (id, body = {}) =>
    apiRequest(`/ai/recommendations/${id}/dismiss/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  resolveAiRecommendation: (id, body = {}) =>
    apiRequest(`/ai/recommendations/${id}/resolve/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reopenAiRecommendation: (id, body = {}) =>
    apiRequest(`/ai/recommendations/${id}/reopen/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listSourceConnections: (projectId) =>
    apiRequest(`/projects/${projectId}/source-connections/`),
  createSourceConnection: (projectId, body) =>
    apiRequest(`/projects/${projectId}/source-connections/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSourceConnection: (projectId, connectionId) =>
    apiRequest(`/projects/${projectId}/source-connections/${connectionId}/`),
  updateSourceConnection: (projectId, connectionId, body) =>
    apiRequest(`/projects/${projectId}/source-connections/${connectionId}/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteSourceConnection: (projectId, connectionId) =>
    apiRequest(`/projects/${projectId}/source-connections/${connectionId}/`, {
      method: 'DELETE',
    }),
  testSourceConnection: (projectId, connectionId) =>
    apiRequest(`/projects/${projectId}/source-connections/${connectionId}/test/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  rotateSourceConnectionSecret: (projectId, connectionId, body) =>
    apiRequest(
      `/projects/${projectId}/source-connections/${connectionId}/rotate-secret/`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    ),
  disableSourceConnection: (projectId, connectionId) =>
    apiRequest(`/projects/${projectId}/source-connections/${connectionId}/disable/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  createRevisionFromPerforce: (projectId, body) =>
    apiRequest(`/projects/${projectId}/revisions/from-perforce/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // AI Rigging / Project Rig Memory (Slice 1)
  getProjectRigMemory: (projectId) => apiRequest(`/projects/${projectId}/rig-memory/`),
  createProjectRigMemory: (projectId, body = {}) =>
    apiRequest(`/projects/${projectId}/rig-memory/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchProjectRigMemory: (projectId, body) =>
    apiRequest(`/projects/${projectId}/rig-memory/`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  listRigProjectPolicies: (projectId) =>
    apiRequest(`/projects/${projectId}/rig-memory/project-policies/`),
  createRigProjectPolicy: (projectId, body) =>
    apiRequest(`/projects/${projectId}/rig-memory/project-policies/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  publishRigProjectPolicy: (projectId, versionId) =>
    apiRequest(
      `/projects/${projectId}/rig-memory/project-policies/${versionId}/publish/`,
      { method: 'POST', body: JSON.stringify({}) }
    ),
  listRigCharacters: (projectId) =>
    apiRequest(`/projects/${projectId}/rig-memory/characters/`),
  createRigCharacter: (projectId, body) =>
    apiRequest(`/projects/${projectId}/rig-memory/characters/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getRigCharacter: (projectId, characterId) =>
    apiRequest(`/projects/${projectId}/rig-memory/characters/${characterId}/`),
  listRigCharacterVersions: (projectId, characterId) =>
    apiRequest(
      `/projects/${projectId}/rig-memory/characters/${characterId}/versions/`
    ),
  listRigDecisions: (projectId) =>
    apiRequest(`/projects/${projectId}/rig-memory/decisions/`),
  listRigProposals: (projectId) =>
    apiRequest(`/projects/${projectId}/rig-memory/proposals/`),
  createRiggingJob: (projectId, body) =>
    apiRequest(`/projects/${projectId}/rigging/jobs/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listRiggingJobs: (projectId, params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiRequest(`/projects/${projectId}/rigging/jobs/${suffix}`);
  },
  listPartnerRiggingJobs: (params = {}) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return apiRequest(`/rigging/jobs/${suffix}`);
  },
  getRiggingJob: (projectId, jobId) =>
    apiRequest(`/projects/${projectId}/rigging/jobs/${jobId}/`),
  previewRiggingJob: (projectId, body) =>
    apiRequest(`/projects/${projectId}/rigging/jobs/preview/`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getRiggingRuntimeAvailability: (projectId) =>
    apiRequest(`/projects/${projectId}/rigging/runtime-availability/`),
  cancelJob: (jobId) =>
    apiRequest(`/bake-jobs/${jobId}/cancel/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

export default bakingApi;
